import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ConfigProvider } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs from 'dayjs';
import 'dayjs/locale/he';

import { AuthProvider } from './auth/AuthContext';
import App from './App';
import './index.css';

dayjs.locale('he');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Single root ConfigProvider. EVERY nested ConfigProvider MUST repeat
          locale={heIL} or antd silently falls back to zh_CN. See
          docs/platform/05-arbox-ui-design-system.md §4. */}
      <ConfigProvider
        direction="rtl"
        locale={heIL}
        // Critical inside the iframe: keeps dropdowns / select menus / popups
        // out of overflow-clipping ancestors.
        getPopupContainer={() => document.body}
        theme={{
          token: {
            colorPrimary: '#1936f7',
            colorError: '#ea3635',
            colorSuccess: '#0d7f56',
            colorWarning: '#eb6e00',
            colorInfo: '#1936f7',
            colorText: '#3b3b3b',
            colorTextSecondary: '#9d9d9d',
            colorBorder: '#e0e0e0',
            colorBgLayout: '#f9f9f9',
            borderRadius: 4,
            fontFamily: 'Ploni, Segoe UI, Tahoma, Arial, sans-serif',
            controlOutlineWidth: 0,
          },
          components: {
            Table: {
              headerBg: '#ffffff',
              headerColor: '#3b3b3b',
              headerSplitColor: 'transparent',
              rowHoverBg: '#f9f9f9',
              borderColor: '#f0f0f0',
              cellPaddingBlock: 12,
              cellPaddingInline: 16,
              fontFamily: 'Ploni, sans-serif',
            },
            Pagination: {
              itemBg: 'transparent',
              itemActiveBg: 'transparent',
              itemSize: 24,
              fontSize: 14,
            },
            Input: {
              activeShadow: 'none',
              activeBorderColor: '#1936f7',
              hoverBorderColor: '#1936f7',
            },
            DatePicker: {
              activeShadow: 'none',
              activeBorderColor: '#1936f7',
              hoverBorderColor: '#1936f7',
              cellHoverBg: '#ECF7FF',
              cellActiveWithRangeBg: '#ECF7FF',
              cellHoverWithRangeBg: '#DFF1FF',
            },
            Select: {
              activeOutlineColor: 'transparent',
            },
            Tabs: {
              // Arbox financial pattern (see דוחות financial screen):
              //   inactive label = #3b3b3b regular
              //   active label   = brand blue, bold (the CSS rule below pushes
              //                    weight to 600 — antd doesn't expose a token)
              //   ink-bar        = brand blue underline
              itemColor: '#3b3b3b',
              itemHoverColor: '#1936f7',
              itemSelectedColor: '#1936f7',
              itemActiveColor: '#1936f7',
              inkBarColor: '#1936f7',
              titleFontSize: 15,
              titleFontSizeSM: 14,
              horizontalItemPadding: '8px 0',
              horizontalItemGutter: 32,
              horizontalMargin: '0 0 12px 0',
            },
          },
        }}
      >
        <AuthProvider>
          <App />
          <Toaster
            position="bottom-center"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: '4px',
                fontFamily: 'Ploni, sans-serif',
                fontSize: '16px',
                direction: 'rtl',
              },
              success: { style: { background: '#4B93F7', color: 'white' } },
              error: { style: { background: '#EA3635', color: 'white' } },
            }}
          />
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
